// Ekran 03 — "Yeni numune" drawer'ı.
// Alanlar numune tipine göre değişir (K22): lot → lot seçimi,
// fermenter → ünite seçimi, ürün → ürün kartı.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DrawerAltlik, DrawerGovde, useDrawer } from '../../components/Drawer'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { trTarihSaat } from '../../lib/trFormat'
import { lotlar as lotApi } from '../intake/api'
import { uniteler as uniteApi } from './uniteApi'
import { numuneler, type NumuneTipi } from './api'

const TIPLER: { anahtar: NumuneTipi; etiket: string }[] = [
  { anahtar: 'lot', etiket: 'Lot' },
  { anahtar: 'fermenter', etiket: 'Fermenter' },
  { anahtar: 'urun', etiket: 'Ürün' },
]

export default function YeniNumuneDrawer() {
  const { kapat } = useDrawer()
  const kuyruk = useQueryClient()
  const [tip, setTip] = useState<NumuneTipi>('lot')
  const [kaynakId, setKaynakId] = useState('')

  const { data: sonraki } = useQuery({
    queryKey: ['numune-sonraki-no'],
    queryFn: numuneler.sonrakiNo,
  })

  const { data: lotListesi = [] } = useQuery({
    queryKey: ['lotlar', 'numune-secimi'],
    queryFn: () => lotApi.liste({ durum: 'onayli' }),
    enabled: tip === 'lot',
  })

  const { data: uniteListesi = [] } = useQuery({
    queryKey: ['uniteler'],
    queryFn: uniteApi.liste,
    enabled: tip === 'fermenter',
  })

  const kaydet = useMutation({
    mutationFn: () =>
      numuneler.olustur({
        tip,
        lotId: tip === 'lot' ? kaynakId : null,
        uniteId: tip === 'fermenter' ? kaynakId : null,
      }),
    onSuccess: (n) => {
      kuyruk.invalidateQueries({ queryKey: ['numuneler'] })
      kuyruk.invalidateQueries({ queryKey: ['lab-ozeti'] })
      kuyruk.invalidateQueries({ queryKey: ['numune-sonraki-no'] })
      kuyruk.invalidateQueries({ queryKey: ['lotlar'] })
      toast(`${n.no} oluşturuldu → analiz kuyruğuna eklendi`)
      kapat()
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Numune oluşturulamadı'),
  })

  // Ürün numunesi bir kaynağa bağlanmıyor: §5.1'de numune yalnız lot veya
  // üniteye bağlanabiliyor (sevk_id sevk modülüyle, Faz 5).
  const gecerli = tip === 'urun' || kaynakId !== ''

  // Alan ipucu "numunesiz lotlar öncelikli" diyor — söz gerçekten tutulsun
  // diye numunesizler başa alınıyor; sunucu sırası (tarih) içeride korunuyor.
  const siraliLotlar = [...lotListesi].sort(
    (a, b) => Number(a.numuneNo !== null) - Number(b.numuneNo !== null),
  )

  return (
    <>
      <DrawerGovde>
        <div className="fld" style={{ marginBottom: 10 }}>
          <label>Numune tipi</label>
          <div className="radios">
            {TIPLER.map((t) => (
              <button
                key={t.anahtar}
                className={tip === t.anahtar ? 'radio-i on' : 'radio-i'}
                type="button"
                onClick={() => { setTip(t.anahtar); setKaynakId('') }}
              >
                {t.etiket}
              </button>
            ))}
          </div>
        </div>

        <div className="fgrid">
          {tip === 'lot' && (
            <div className="fld" style={{ gridColumn: '1/-1' }}>
              <label>Lot</label>
              <select className="in" value={kaynakId} onChange={(e) => setKaynakId(e.target.value)}>
                <option value="">Seçin…</option>
                {siraliLotlar.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.belgeNo} · {l.stokAd} · {l.cariUnvan}
                    {l.numuneNo === null ? ' (numunesiz)' : ''}
                  </option>
                ))}
              </select>
              <div className="fld-hint">Numunesiz onaylı lotlar öncelikli listelenir</div>
            </div>
          )}

          {tip === 'fermenter' && (
            <div className="fld" style={{ gridColumn: '1/-1' }}>
              <label>Ünite</label>
              <select className="in" value={kaynakId} onChange={(e) => setKaynakId(e.target.value)}>
                <option value="">Seçin…</option>
                {uniteListesi.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.kod} · {u.ad}
                  </option>
                ))}
              </select>
              <div className="fld-hint">
                Fermenter numunesi proses takibine işler; stok etkisi yok (K21)
              </div>
            </div>
          )}

          {tip === 'urun' && (
            <div className="fld" style={{ gridColumn: '1/-1' }}>
              <div className="fld-hint">Ürün numunesi depo kalite verisini besler (K22)</div>
              <div className="fld-hint">
                Sevk kaydına bağlanma (K22) Faz 5'te geliyor — şimdilik bağlantısız açılıyor.
              </div>
            </div>
          )}

          <div className="fld">
            <label>Numune no</label>
            <input className="in mono" readOnly value={sonraki?.no ?? '…'} />
          </div>
          <div className="fld">
            <label>Alınma zamanı</label>
            <input className="in mono" readOnly value={trTarihSaat(new Date())} />
          </div>
        </div>
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={() => kapat()}>
          Vazgeç
        </button>
        <button
          className="btn btn-pri"
          type="button"
          disabled={!gecerli || kaydet.isPending}
          title={gecerli ? undefined : 'Kaynak seçilmeli'}
          onClick={() => kaydet.mutate()}
        >
          {kaydet.isPending ? 'Kaydediliyor…' : 'Numuneyi kaydet'}
        </button>
      </DrawerAltlik>
    </>
  )
}
